import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useContext, useState } from 'react';
import { Accordion } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { SelectedVehicleContext } from '../../../contexts/vehicle/SelectedVehicleContext.ts';
import { patchVehicle } from '../../../queries/getVehicles.ts';
import { IVehicle } from '../../../schema/Vehicle.ts';
import { useTerritoryConsolidatedGeography } from '../../../services/Geography.ts';
import { useTerritory } from '../../../services/Territory.ts';
import { useIntegrationsByVehicle } from '../../../services/Vehicle.ts';
import { formatShiftDates } from '../../../utils/fleetUtils.ts';
import { IconButton, LoadingButton } from '../../ui/Button.tsx';
import { Log } from '../Logs/Log.tsx';
import { InOperation } from './AccordionHeaders/InOperation.tsx';
import { NoOperation } from './AccordionHeaders/NoOperation.tsx';
import { PlannedOperation } from './AccordionHeaders/PlannedOperation.tsx';
import { AutomataFeatures } from './AutomataFeatures.tsx';
import { AutomataLevel } from './AutomataLevel.tsx';
import { Battery } from './Battery.tsx';
import { Door } from './Door.tsx';
import { ChangeShiftModal } from './Modal/ChangeShiftModal.tsx';
import { CreateMissionModal } from './Modal/CreateMissionModal.tsx';
import { VehicleDisplayIdentifierModal } from './Modal/VehicleDisplayIdentifierModal.tsx';

interface Props {
  vehicle: IVehicle;
  groupName: 'IN_OPERATION' | 'PLANNED_OPERATION' | 'NO_OPERATION';
}

export type VehicleMutationType = UseMutationResult<
  AxiosResponse<unknown>,
  Error,
  { automata_state: string },
  void
>;

export function Vehicle(props: Readonly<Props>) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { setSelectedVehicle } = useContext(SelectedVehicleContext);
  const params = useParams() as { territorySlug: string };
  const { data: geography } = useTerritoryConsolidatedGeography(params.territorySlug);
  const [showCreateMission, setShowCreateMission] = useState(false);
  const [showAutomataFeatures, setShowAutomataFeatures] = useState(false);
  const [showLogs, setShowLogs] = useState<boolean>(false);
  const [showDisplayIdentifierModal, setShowDisplayIdentifierModal] = useState(false);
  const [switchVehicleMode, setSwitchVehicleMode] = useState(false);
  const { data: territory } = useTerritory(params.territorySlug);
  const { data: integrations } = useIntegrationsByVehicle(params.territorySlug, props.vehicle.id);
  const hasMultipleIntegrations = (integrations?.length ?? 0) > 1;
  const onEnter = () => {
    setSelectedVehicle(props.vehicle);
  };
  const onExit = () => {
    setSelectedVehicle(undefined);
  };

  const headerMapping = {
    IN_OPERATION: InOperation,
    PLANNED_OPERATION: PlannedOperation,
    NO_OPERATION: NoOperation,
  };
  const HeaderComponent = headerMapping[props.groupName];

  const vehicleMutation: VehicleMutationType = useMutation({
    mutationFn: (payload: { automata_state: string }) =>
      patchVehicle(params.territorySlug, props.vehicle.id, payload),
    onMutate: async data => {
      await queryClient.cancelQueries({ queryKey: ['vehicles', params.territorySlug] });
      const previousVehicles = queryClient.getQueryData<{ data?: IVehicle[] }>([
        'vehicles',
        params.territorySlug,
      ]);
      const optimisticVehicles = previousVehicles?.data?.map(vehicle =>
        vehicle.id === props.vehicle.id ? { ...vehicle, automata_state: data.automata_state } : vehicle,
      );
      queryClient.setQueryData(['vehicles', params.territorySlug], {
        ...previousVehicles,
        data: optimisticVehicles,
      });
    },
    onError: () => {
      toast.error(
        `${t('vehicle.vehicle')} ${props.vehicle.display_identifier} ${t('vehicle.automata-update-error')}`,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', params.territorySlug] });
    },
  });

  return (
    <Accordion.Item
      eventKey={props.vehicle.id.toString()}
      className={'basic-shadow rounded-3 mt-2'}
      style={{ minWidth: '200px' }}>
      <Accordion.Header className={'p-3'}>
        <HeaderComponent vehicle={props.vehicle} />
      </Accordion.Header>
      <Accordion.Body
        onEntered={onEnter}
        onExit={onExit}
        className={'mt-0 pt-0 d-flex flex-column gap-3'}>
        <>
          <div className="d-flex flex-column gap-2">
            <p className={'card-text'}>{t('vehicle.subtitle-identity')}</p>
            <div className="ms-2 gap-1 d-flex flex-column">
              <div className="d-flex gap-1 align-items-center justify-content-start">
                <p className="text-basic d-flex flex-row align-items-center">
                  {props.vehicle.display_identifier}
                </p>
                <IconButton
                  imagePath={'/theme/media/pencil-black.svg'}
                  onClick={() => setShowDisplayIdentifierModal(true)}
                  alt="Vehicle name"
                />
              </div>
              <p className={'text-basic'}>
                {props.vehicle.shuttle_provider_identifier} {t('vehicle.by')} {props.vehicle.provider}
              </p>
            </div>
          </div>
          {props.vehicle.shift && (
            <>
              <div className="d-flex flex-column gap-2">
                <div className="d-flex gap-2 align-items-center justify-content-start">
                  <p className={'card-text'}>{t('vehicle.subtitle-shift')}</p>
                  {hasMultipleIntegrations && (
                    <IconButton
                      imagePath={'/theme/media/settings-logo.svg'}
                      onClick={() => setSwitchVehicleMode(true)}
                      alt="Vehicle name"
                    />
                  )}
                </div>
                <div className="ms-2 gap-1 d-flex flex-column">
                  <div className="d-flex gap-1 align-items-center justify-content-start">
                    <img src={'/theme/media/shift.svg'} width="18" height="18" alt="Shift icon" />
                    <p className={'text-bold shift-color'}>{props.vehicle.shift.id}</p>
                    <p className={'text-basic'}>
                      {t('vehicle.by')} {props.vehicle.shift.dispatch_provider}
                    </p>
                  </div>
                  <p className={'text-basic'}>{formatShiftDates(props.vehicle.shift)}</p>
                </div>
              </div>
            </>
          )}
          <div className="d-flex flex-column gap-2">
            <p className={'card-text'}>{t('vehicle.subtitle-live-state')}</p>
            <div className="d-flex ms-2 align-items-center justify-content-between">
              <div className="col">
                <Battery batteryLevel={Number(props.vehicle.charge_percentage)} />
              </div>
              <div className="col">
                <Door doorStatus={props.vehicle.door_status} />
              </div>
            </div>
          </div>
          <div className="d-flex flex-column gap-2">
            <p className={'card-text'}>{t('vehicle.subtitle-orchestration')}</p>
            <div className="d-flex ms-2 gap-2 align-items-center justify-content-start">
              <AutomataLevel vehicle={props.vehicle} mutation={vehicleMutation} />
              <IconButton
                imagePath={'/theme/media/settings-logo.svg'}
                onClick={() => setShowAutomataFeatures(true)}
                alt="Vehicle name"
                size={25}
              />
            </div>
            <div className="d-flex ms-2 flew-wrap align-items-center justify-content-between">
              <p className="text-basic col">{props.vehicle.supervision_mode}</p>
              <p className="text-basic col">{props.vehicle.robot_mode}</p>
            </div>
          </div>
          <div className="d-flex flex-wrap justify-content-between mt-1 gap-2">
            {territory?.permissions.can_create_missions && (
              <LoadingButton
                type={'submit-button'}
                loading={false}
                imagePath={'/theme/media/plus.svg'}
                text={t('vehicle.create-mission-button')}
                onClick={() => setShowCreateMission(true)}
              />
            )}
            <LoadingButton
              type={'submit-button'}
              loading={false}
              imagePath={'/theme/media/log.svg'}
              text={t('logs.button')}
              onClick={() => setShowLogs(true)}
            />
          </div>
          {showLogs && <Log vehicle={props.vehicle} showLogs={showLogs} setShowLogs={setShowLogs} />}
        </>
        <AutomataFeatures
          vehicle={props.vehicle}
          showAutomataFeatures={showAutomataFeatures}
          setShowAutomataFeatures={setShowAutomataFeatures}
        />
        {showCreateMission && (
          <CreateMissionModal
            key={showCreateMission ? 'open' : 'closed'}
            show={showCreateMission}
            onHide={() => setShowCreateMission(false)}
            vehicleId={props.vehicle.id}
            vehicleDisplayIdentifier={props.vehicle.display_identifier}
            nodes={geography}
          />
        )}
        <VehicleDisplayIdentifierModal
          vehicle={props.vehicle}
          showDisplayIdentifierModal={showDisplayIdentifierModal}
          setShowDisplayIdentifierModal={setShowDisplayIdentifierModal}
        />
        {hasMultipleIntegrations && switchVehicleMode && (
          <ChangeShiftModal
            vehicleId={props.vehicle.id}
            show={switchVehicleMode}
            onHide={() => setSwitchVehicleMode(false)}
          />
        )}
      </Accordion.Body>
    </Accordion.Item>
  );
}
